/**
 * Staff-side inbox for customer portal Order Requests. Accept converts the
 * request straight into a Quotation (pre-filled grade/site, priced at the
 * rate entered here) — staff finalises terms on the quotation itself, then
 * Gen Order as normal. See routers/customer_portal.py.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Check, X as XIcon, ChevronRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPatch } from '../../lib/api';
import { formatISTDateTime } from '../../lib/format';

const STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};
const FILTERS = ['pending', 'accepted', 'rejected', 'all'];

export default function PortalRequestsInbox() {
  const [filter, setFilter] = useState('pending');
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    apiGet('/portal/admin/order-requests', filter === 'all' ? {} : { status: filter })
      .then((r) => { setItems(r.items || []); setPendingCount(r.pending_count || 0); })
      .catch(() => toast.error('Failed to load order requests'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  return (
    <div data-testid="portal-requests-inbox">
      <PageHeader overline="Business Development" title="Customer Order Requests"
        subtitle="Supply requests submitted through the customer portal." />
      <div className="flex items-center gap-1 mb-4 border-b border-slate-200">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`relative px-4 py-2.5 text-sm font-display font-semibold capitalize ${filter === f ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]'}`}>
            {f}{f === 'pending' && pendingCount > 0 ? ` (${pendingCount})` : ''}
            {filter === f && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#1F7A6E]" />}
          </button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr>
            <th>Request No</th><th>Company</th><th>Grade / Qty</th><th>Requested</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan="6" className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400">No {filter !== 'all' ? filter : ''} requests.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setActive(r)} data-testid={`portal-request-row-${r.id}`}>
                <td className="font-mono text-xs text-[#1F7A6E] font-semibold">{r.request_no}</td>
                <td className="font-display font-bold">{r.company_name}</td>
                <td>{r.grade} · {r.qty_m3} m³</td>
                <td className="text-xs">{r.requested_date}</td>
                <td><span className={`tag ${STATUS_COLOR[r.status]}`}>{r.status}</span></td>
                <td className="text-right"><ChevronRight size={14} className="text-slate-300" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {active && <RequestDetailModal request={active} onClose={() => setActive(null)} onDecided={() => { setActive(null); load(); }} />}
    </div>
  );
}

function RequestDetailModal({ request, onClose, onDecided }) {
  const [saving, setSaving] = useState(false);
  const [rate, setRate] = useState('');
  const [notes, setNotes] = useState('');

  const decide = async (action) => {
    if (action === 'reject' && !notes.trim()) return toast.error('Add a reason so the customer sees why');
    setSaving(true);
    try {
      const res = await apiPatch(`/portal/admin/order-requests/${request.id}`, {
        action, staff_notes: notes || null, rate_per_m3: action === 'accept' ? Number(rate || 0) : 0,
      });
      toast.success(action === 'accept' ? `Accepted — ${res.converted_quotation_id ? 'quotation created' : 'added to the sales pipeline'}` : 'Request rejected');
      onDecided(res);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to decide'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div className="font-display font-bold text-sm">{request.request_no}</div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"><XIcon size={16} /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-slate-400 text-xs">Company</div><span className="font-display font-bold">{request.company_name}</span></div>
            <div><div className="text-slate-400 text-xs">Status</div><span className={`tag ${STATUS_COLOR[request.status]}`}>{request.status}</span></div>
            <div><div className="text-slate-400 text-xs">Grade / Qty</div>{request.grade} · {request.qty_m3} m³</div>
            <div><div className="text-slate-400 text-xs">Requested Date</div>{request.requested_date}</div>
            <div className="col-span-2"><div className="text-slate-400 text-xs">Site Address</div>{request.site_address}</div>
            {request.jobsite && <div className="col-span-2"><div className="text-slate-400 text-xs">Site Name</div>{request.jobsite}</div>}
            <div><div className="text-slate-400 text-xs">Contact</div>{request.contact_name || '—'}</div>
            <div><div className="text-slate-400 text-xs">Phone</div>{request.contact_phone || '—'}</div>
          </div>
          {request.notes && <div className="bg-slate-50 border border-slate-200 rounded-sm p-2 text-xs text-slate-600">{request.notes}</div>}
          <div className="text-[11px] text-slate-400">Submitted {formatISTDateTime(request.created_at)}</div>

          {request.status === 'pending' ? (
            <div className="border-t border-slate-100 pt-3 space-y-3">
              <div>
                <label className="label">Rate (₹ / m³) — for accept</label>
                <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} className="input" placeholder="e.g. 5800" />
              </div>
              <div>
                <label className="label">Notes {`(required to reject)`}</label>
                <input value={notes} onChange={(e) => setNotes(e.target.value)} className="input" placeholder="Confirmed for plant capacity / Outside delivery radius…" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => decide('accept')} disabled={saving} className="btn-primary flex-1 justify-center flex items-center gap-1" data-testid="portal-request-accept">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <><Check size={13} /> Accept → Create Quotation</>}
                </button>
                <button onClick={() => decide('reject')} disabled={saving} className="btn-secondary flex-1 justify-center flex items-center gap-1" data-testid="portal-request-reject">
                  <XIcon size={13} /> Reject
                </button>
              </div>
            </div>
          ) : (
            <div className="border-t border-slate-100 pt-3 space-y-2">
              {request.quotation && (
                <div className="text-sm">
                  Converted to <span className="font-mono text-[#1F7A6E] font-semibold">{request.quotation.quotation_no}</span>
                  {' '}<span className="tag bg-slate-100 text-slate-600">{request.quotation.status}</span>
                </div>
              )}
              {request.staff_notes && (
                <div>
                  <div className="text-xs text-slate-400 mb-1">Decision notes</div>
                  <div className="text-sm">{request.staff_notes}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
